import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import SavingsIcon from '@mui/icons-material/Savings';

// Two-step consent dialog for enrolling a client in prepaid billing.
//
// 1. On open we fetch the active terms (version + body + computed initial
//    deposit) from the API so the version we submit back on accept matches
//    what the user actually read.
// 2. The body is shown in a scrollable, fixed-height container so long
//    legal text doesn't collapse the rest of the dialog.
// 3. The "Authorize" button is gated by a checkbox — clicking the body
//    isn't acceptance; ticking the box is.
// 4. The submitted POST includes ``{ terms_version }``; the backend
//    rejects with 409 ``terms_version_mismatch`` if it doesn't match the
//    current PREPAID_TERMS_VERSION, so a stale tab can't enroll under
//    outdated terms.
//
// The component itself is presentation-only: parents pass ``fetchTerms``
// and ``onSubmit`` callbacks so the same dialog backs both the staff page
// (calls /api/dashboard/billing-profiles/X/...) and the client page
// (calls /api/dashboard/my-billing-profiles/X/...).
const formatCurrency = (value) => {
  const num = Number(value || 0);
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function EnablePrepaidConfirmDialog({
  open,
  fetchTerms,            // async () => { data: { version, body, initial_deposit_amount, owner_name } }
  onSubmit,              // async ({ termsVersion }) => void; throws on backend reject
  onClose,
}) {
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Reset everything every time the dialog opens — stale state from a
  // prior cancellation would otherwise mis-pair the checkbox/version.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setTerms(null);
    setLoadError(null);
    setConfirmed(false);
    setSubmitError(null);
    setSubmitting(false);
    setLoading(true);
    fetchTerms()
      .then((resp) => {
        if (cancelled) return;
        setTerms(resp?.data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err?.response?.data?.detail ||
            err?.message ||
            'Could not load the prepaid terms.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, fetchTerms]);

  const depositLabel = useMemo(() => {
    if (!terms?.initial_deposit_amount) return '';
    return formatCurrency(terms.initial_deposit_amount);
  }, [terms]);

  const handleAuthorize = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit({ termsVersion: terms.version });
      // Parent closes the dialog on success — we leave submitting true so
      // the button stays disabled during the unmount transition.
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === 'terms_version_mismatch') {
        // Terms were bumped while this tab was open. Re-fetch + force the
        // user to re-confirm so we never accept against stale text.
        setConfirmed(false);
        setSubmitError(
          'These terms were updated while you were reviewing. Reloaded ' +
            'the latest version — please re-confirm.',
        );
        try {
          const resp = await fetchTerms();
          setTerms(resp?.data || null);
        } catch {
          /* swallow — primary error already surfaced */
        }
      } else {
        setSubmitError(
          err?.response?.data?.detail ||
            err?.message ||
            'Could not enable prepaid billing.',
        );
      }
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <SavingsIcon color="primary" />
          <span>Enable prepaid billing</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        )}
        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}
        {terms && (
          <Stack spacing={2}>
            <Alert severity="info" icon={<SavingsIcon />}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>
                  Authorizing an initial deposit of{' '}
                  {depositLabel ? <>{depositLabel}</> : 'a calculated amount'}{' '}
                  via ACH.
                </strong>
              </Typography>
              <Typography variant="body2">
                {terms.owner_name
                  ? `For ${terms.owner_name}. `
                  : ''}
                Funds typically settle in 3–5 business days. Your current
                billing keeps running until then. Once settled, ad spend
                draws from the prepaid balance and we replenish via ACH
                automatically when the balance dips below the floor.
              </Typography>
            </Alert>

            <Typography variant="overline" color="text.secondary">
              Terms — version {terms.version}
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                maxHeight: 320,
                overflowY: 'auto',
                backgroundColor: 'background.paper',
                fontFamily: 'monospace',
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.55,
              }}
              tabIndex={0}
            >
              {terms.body}
            </Box>

            {submitError && <Alert severity="error">{submitError}</Alert>}

            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  disabled={submitting}
                />
              }
              label={
                <Typography variant="body2">
                  I have read and agree to the prepaid billing terms above,
                  and I authorize the initial ACH debit of{' '}
                  <strong>{depositLabel || 'the calculated amount'}</strong>{' '}
                  against the bank account on file, plus recurring
                  replenishment debits as described.
                </Typography>
              }
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} autoFocus>
          Cancel
        </Button>
        <Button
          onClick={handleAuthorize}
          color="primary"
          variant="contained"
          disabled={!terms || !confirmed || submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : null
          }
        >
          {submitting
            ? 'Authorizing…'
            : `Authorize ${depositLabel || 'initial deposit'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
